import React from "react";
import styled from "styled-components";
import { MessageSquare, Send } from "lucide-react";
import DashboardLayout from "layouts/DashboardLayout";
import { EmptyState } from "components/dashboard";

const C_PRIMARY = "#ff6c00";

// ─── Styled ────────────────────────────────────────────────────────────────────

const Layout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1px;
  background: #e5e7eb;
  border: 1px solid #e5e7eb;
  height: calc(100vh - 140px);
  min-height: 400px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const Sidebar = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #9ca3af;
`;

const ConvItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border: none;
  border-bottom: 1px solid #f9fafb;
  background: ${(p) => (p.$active ? "#fff8f3" : "white")};
  cursor: pointer;
  text-align: left;
  width: 100%;
  border-left: 3px solid ${(p) => (p.$active ? C_PRIMARY : "transparent")};
  transition: background 0.12s;
  &:hover {
    background: #fafafa;
  }
`;

const ConvAvatar = styled.div`
  width: 36px;
  height: 36px;
  background: ${C_PRIMARY};
  color: white;
  font-size: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ConvInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
const ConvName = styled.p`
  font-size: 0.82rem;
  font-weight: 700;
  color: #111;
`;
const ConvPreview = styled.p`
  font-size: 0.72rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;
const ConvTime = styled.span`
  font-size: 0.65rem;
  color: #9ca3af;
  flex-shrink: 0;
`;

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  background: ${C_PRIMARY};
  display: inline-block;
  flex-shrink: 0;
  margin-top: 6px;
`;

const ChatArea = styled.div`
  background: #fafafa;
  display: flex;
  flex-direction: column;
`;

const ChatHeader = styled.div`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Bubble = styled.div<{ $mine?: boolean }>`
  max-width: 68%;
  align-self: ${(p) => (p.$mine ? "flex-end" : "flex-start")};
  background: ${(p) => (p.$mine ? C_PRIMARY : "white")};
  color: ${(p) => (p.$mine ? "white" : "#111")};
  border: 1px solid ${(p) => (p.$mine ? "transparent" : "#e5e7eb")};
  padding: 0.65rem 1rem;
  font-size: 0.85rem;
  line-height: 1.5;
`;

const BubbleMeta = styled.p<{ $mine?: boolean }>`
  font-size: 0.65rem;
  color: #9ca3af;
  margin-top: 4px;
  text-align: ${(p) => (p.$mine ? "right" : "left")};
`;

const ChatInput = styled.div`
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 1rem 1.5rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
`;

const Textarea = styled.textarea`
  flex: 1;
  resize: none;
  border: 1.5px solid #e5e7eb;
  padding: 0.65rem 0.875rem;
  font-size: 0.875rem;
  font-family: inherit;
  color: #111;
  rows: 2;
  &:focus {
    outline: none;
    border-color: ${C_PRIMARY};
  }
`;

const SendBtn = styled.button`
  background: ${C_PRIMARY};
  color: white;
  border: none;
  padding: 0.65rem 1.25rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  transition: background 0.15s;
  &:hover {
    background: #e55c00;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Mock data ──────────────────────────────────────────────────────────────────

const MOCK_CONVERSATIONS = [
  {
    id: 1,
    name: "Conseiller MomsEduc",
    initials: "ME",
    preview: "Votre dossier a bien été reçu.",
    time: "14:32",
    unread: true,
    messages: [
      {
        id: 1,
        text: "Bonjour, votre dossier a bien été reçu et est en cours de traitement.",
        mine: false,
        time: "14:30",
      },
      {
        id: 2,
        text: "Merci beaucoup ! Combien de temps cela prend-il ?",
        mine: true,
        time: "14:31",
      },
      {
        id: 3,
        text: "Généralement entre 5 et 10 jours ouvrés.",
        mine: false,
        time: "14:32",
      },
    ],
  },
  {
    id: 2,
    name: "Support technique",
    initials: "ST",
    preview: "Votre demande a été prise en compte.",
    time: "Hier",
    unread: false,
    messages: [
      {
        id: 1,
        text: "Votre demande de support a été prise en compte. Nous revenons vers vous sous 24h.",
        mine: false,
        time: "Hier 10:15",
      },
    ],
  },
];

// ─── Component ─────────────────────────────────────────────────────────────────

const MessagesPage: React.FC = () => {
  const [activeId, setActiveId] = React.useState<number>(1);
  const [input, setInput] = React.useState("");

  const active = MOCK_CONVERSATIONS.find((c) => c.id === activeId);

  return (
    <DashboardLayout title="Messages">
      {MOCK_CONVERSATIONS.length === 0 ? (
        <EmptyState
          icon={<MessageSquare size={40} />}
          title="Aucun message"
          description="Vos échanges avec l'équipe MomsEduc apparaîtront ici."
        />
      ) : (
        <Layout>
          {/* Sidebar conversations */}
          <Sidebar>
            <SidebarHeader>Conversations</SidebarHeader>
            {MOCK_CONVERSATIONS.map((c) => (
              <ConvItem
                key={c.id}
                $active={c.id === activeId}
                onClick={() => setActiveId(c.id)}
              >
                <ConvAvatar>{c.initials}</ConvAvatar>
                <ConvInfo>
                  <ConvName>{c.name}</ConvName>
                  <ConvPreview>{c.preview}</ConvPreview>
                </ConvInfo>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <ConvTime>{c.time}</ConvTime>
                  {c.unread && <UnreadDot />}
                </div>
              </ConvItem>
            ))}
          </Sidebar>

          {/* Chat */}
          {active && (
            <ChatArea>
              <ChatHeader>
                <ConvAvatar
                  style={{ width: 32, height: 32, fontSize: "0.65rem" }}
                >
                  {active.initials}
                </ConvAvatar>
                <div>
                  <p
                    style={{
                      fontWeight: 800,
                      fontSize: "0.9rem",
                      color: "#111",
                    }}
                  >
                    {active.name}
                  </p>
                  <p style={{ fontSize: "0.7rem", color: "#9ca3af" }}>
                    MomsEduc
                  </p>
                </div>
              </ChatHeader>

              <ChatMessages>
                {active.messages.map((m) => (
                  <div key={m.id}>
                    <Bubble $mine={m.mine}>{m.text}</Bubble>
                    <BubbleMeta $mine={m.mine}>{m.time}</BubbleMeta>
                  </div>
                ))}
              </ChatMessages>

              <ChatInput>
                <Textarea
                  rows={2}
                  placeholder="Écrire un message…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      setInput("");
                    }
                  }}
                />
                <SendBtn disabled={!input.trim()} onClick={() => setInput("")}>
                  <Send size={14} /> Envoyer
                </SendBtn>
              </ChatInput>
            </ChatArea>
          )}
        </Layout>
      )}
    </DashboardLayout>
  );
};

export default MessagesPage;
